import * as THREE from 'three';

/**
 * A compact, procedural win portal. Its apparent motion is a deterministic
 * force field evaluated from the simulation tick: render polish only, never
 * state, collision, or a source of non-determinism for the simulation.
 */
const PARTICLE_COUNT = 52;
const ARC_COUNT = 14;
const ARC_SEGMENTS = 7;
const EXIT_BURST_DURATION = 0.75;

interface ParticleSeed {
  angle: number;
  radius: number;
  speed: number;
  phase: number;
}

function seededUnit(index: number): number {
  const n = Math.sin(index * 91.173 + 17.43) * 43758.5453;
  return n - Math.floor(n);
}

export class PortalEffect {
  readonly object3D = new THREE.Group();

  private readonly apertureMaterial: THREE.MeshBasicMaterial;
  private readonly innerRingMaterial: THREE.MeshBasicMaterial;
  private readonly outerRingMaterial: THREE.MeshBasicMaterial;
  private readonly arcGeometry: THREE.BufferGeometry;
  private readonly arcMaterial: THREE.LineBasicMaterial;
  private readonly particleGeometry: THREE.BufferGeometry;
  private readonly particleMaterial: THREE.PointsMaterial;
  private readonly exitRingMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly exitRings: THREE.Mesh[] = [];
  private readonly particles: readonly ParticleSeed[];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private exitBurstRemaining = 0;

  constructor() {
    this.object3D.name = 'moonseed-win-portal';
    this.object3D.renderOrder = 2;

    this.apertureMaterial = new THREE.MeshBasicMaterial({
      color: 0x70ddff,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const apertureGeometry = new THREE.CircleGeometry(0.37, 28);
    this.geometries.push(apertureGeometry);
    const aperture = new THREE.Mesh(apertureGeometry, this.apertureMaterial);
    aperture.name = 'portal-aperture';
    aperture.position.z = 0.01;
    this.object3D.add(aperture);

    this.innerRingMaterial = new THREE.MeshBasicMaterial({
      color: 0xe8fbff,
      transparent: true,
      opacity: 0.45,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const innerRingGeometry = new THREE.RingGeometry(0.31, 0.345, 32);
    this.geometries.push(innerRingGeometry);
    const innerRing = new THREE.Mesh(innerRingGeometry, this.innerRingMaterial);
    innerRing.name = 'portal-inner-electric-ring';
    innerRing.position.z = 0.025;
    this.object3D.add(innerRing);

    this.outerRingMaterial = new THREE.MeshBasicMaterial({
      color: 0x069dff,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const outerRingGeometry = new THREE.RingGeometry(0.37, 0.445, 32);
    this.geometries.push(outerRingGeometry);
    const outerRing = new THREE.Mesh(outerRingGeometry, this.outerRingMaterial);
    outerRing.name = 'portal-plasma-rim';
    outerRing.position.z = 0.02;
    this.object3D.add(outerRing);

    this.arcGeometry = new THREE.BufferGeometry();
    this.geometries.push(this.arcGeometry);
    this.arcGeometry.setAttribute('position', new THREE.Float32BufferAttribute(ARC_COUNT * ARC_SEGMENTS * 6, 3));
    this.arcMaterial = new THREE.LineBasicMaterial({
      color: 0xb9f6ff,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const arcs = new THREE.LineSegments(this.arcGeometry, this.arcMaterial);
    arcs.name = 'portal-lightning-arcs';
    arcs.position.z = 0.04;
    this.object3D.add(arcs);

    this.particleGeometry = new THREE.BufferGeometry();
    this.geometries.push(this.particleGeometry);
    this.particleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(PARTICLE_COUNT * 3, 3));
    this.particleMaterial = new THREE.PointsMaterial({
      color: 0xb9f6ff,
      size: 0.045,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const sparks = new THREE.Points(this.particleGeometry, this.particleMaterial);
    sparks.name = 'portal-plasma-particles';
    sparks.position.z = 0.05;
    this.object3D.add(sparks);

    // These rings share the portal's local coordinate system, expanding from
    // the entrance the player selected rather than from a screen overlay.
    for (let i = 0; i < 3; i++) {
      const geometry = new THREE.RingGeometry(0.88, 1, 36);
      const material = new THREE.MeshBasicMaterial({
        color: i === 1 ? 0xffc65c : 0x8be9fd,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(geometry, material);
      ring.visible = false;
      ring.position.z = 0.08 + i * 0.005;
      this.geometries.push(geometry);
      this.exitRingMaterials.push(material);
      this.exitRings.push(ring);
      this.object3D.add(ring);
    }

    this.particles = Array.from({ length: PARTICLE_COUNT }, (_, index) => ({
      angle: seededUnit(index * 4 + 1) * Math.PI * 2,
      radius: 0.38 + seededUnit(index * 4 + 2) * 0.27,
      speed: 0.7 + seededUnit(index * 4 + 3) * 1.1,
      phase: seededUnit(index * 4 + 4) * Math.PI * 2,
    }));
  }

  /** Starts a visual-only burst once the simulation confirms a room clear. */
  startExitBurst(): void {
    this.exitBurstRemaining = EXIT_BURST_DURATION;
  }

  /** Updates the visual force field. `tick` is the sole time input. */
  sync(
    portal: { tx: number; ty: number; hidden: boolean },
    isOpen: boolean,
    tick: number,
    deltaSeconds = 0,
  ): void {
    this.object3D.visible = !portal.hidden;
    if (portal.hidden) return;

    const time = tick * 0.035;
    const energy = isOpen ? 1 : 0.28;
    this.exitBurstRemaining = Math.max(0, this.exitBurstRemaining - deltaSeconds);
    const burst = 1 - this.exitBurstRemaining / EXIT_BURST_DURATION;
    this.object3D.position.set(portal.tx + 0.5, -(portal.ty + 0.5), 0.3);
    this.object3D.rotation.z = time * (isOpen ? 0.24 : 0.08);
    const pulse = 1 + Math.sin(time * 4.1) * 0.045 * energy;
    this.object3D.scale.setScalar(pulse);

    this.apertureMaterial.opacity = 0.09 + energy * (0.13 + Math.sin(time * 3.2) * 0.04);
    this.innerRingMaterial.opacity = 0.14 + energy * 0.68;
    this.outerRingMaterial.opacity = 0.08 + energy * 0.38;
    this.arcMaterial.opacity = 0.05 + energy * 0.72;
    this.particleMaterial.opacity = 0.04 + energy * 0.68;
    this.particleMaterial.size = 0.025 + energy * 0.035;
    for (let i = 0; i < this.exitRings.length; i++) {
      const ring = this.exitRings[i]!;
      const material = this.exitRingMaterials[i]!;
      ring.visible = this.exitBurstRemaining > 0;
      if (!ring.visible) continue;
      const progress = Math.max(0, Math.min(1, burst - i * 0.08));
      const ease = 1 - (1 - progress) ** 3;
      ring.scale.setScalar(0.24 + ease * (1.45 + i * 0.32));
      material.opacity = (1 - progress) * (0.78 - i * 0.12);
    }

    const arcPositions = this.arcGeometry.getAttribute('position') as THREE.Float32BufferAttribute;
    let arcOffset = 0;
    for (let arc = 0; arc < ARC_COUNT; arc++) {
      const baseAngle = (arc / ARC_COUNT) * Math.PI * 2 + Math.sin(time * 0.7 + arc) * 0.12;
      for (let segment = 0; segment < ARC_SEGMENTS; segment++) {
        const writePoint = (progress: number): void => {
          const turbulence = Math.sin(time * 4 + arc * 2.17 + progress * 19) * 0.035 * energy;
          const radius = 0.33 + progress * (0.08 + turbulence);
          const angle = baseAngle + turbulence * 2.5 + Math.sin(progress * 11 + arc) * 0.025;
          arcPositions.setXYZ(arcOffset++, Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
        };
        writePoint(segment / ARC_SEGMENTS);
        writePoint((segment + 1) / ARC_SEGMENTS);
      }
    }
    arcPositions.needsUpdate = true;

    const particlePositions = this.particleGeometry.getAttribute('position') as THREE.Float32BufferAttribute;
    this.particles.forEach((particle, index) => {
      // Radial drift, orbital force and a bounded curl perturbation make the
      // particles feel expelled from a living electrical field without a
      // mutable particle simulation or per-frame allocations.
      const life = (time * particle.speed + particle.phase) % 1;
      const radius = particle.radius + life * 0.2 + burst * 0.62 + Math.sin(time * 5 + particle.phase) * 0.025;
      const angle = particle.angle + time * (1.1 + particle.speed * 0.4) + Math.sin(time * 3 + particle.phase) * 0.18;
      particlePositions.setXYZ(index, Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
    });
    particlePositions.needsUpdate = true;
  }

  dispose(): void {
    for (const geometry of this.geometries) geometry.dispose();
    this.apertureMaterial.dispose();
    this.innerRingMaterial.dispose();
    this.outerRingMaterial.dispose();
    this.arcMaterial.dispose();
    this.particleMaterial.dispose();
    for (const material of this.exitRingMaterials) material.dispose();
  }
}
